import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { addIcons } from 'ionicons';
import { bedOutline, personCircleOutline, serverOutline, arrowForward, warningOutline, walkOutline, alertCircle } from 'ionicons/icons';
import { catchError, combineLatest, take, tap } from 'rxjs';
import { AlertsService, TimestampFilter } from '../../../../core/services/alerts.service';
import { FacilitiesService } from '../../../../core/services/facilities.service';
import { IonicColors } from '../../../../shared/enums/ionicColors.enum';
import { ToastService } from '../../../../core/services/utils/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { INotificationData } from '../../../../shared/models/be/notification';
import { UserRole } from '../../../../shared/models/be/user';
import { MatIcon } from "@angular/material/icon";
import { GuideService } from '../../../../core/services/guide.service';

// Interfaccia semplificata per Mobile
interface MobileAlert {
  id: string;
  dateAlert: Date;
  dateManaged: Date;
  entity: string; // "CADUTA", "USCITA LETTO", etc.
  room: string;
  bed: string;
  managedBy: string;
}

@Component({
  selector: 'app-history-mobile',
  templateUrl: './alertsHistory.mobile.component.html',
  styleUrls: ['./alertsHistory.mobile.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, MatProgressSpinnerModule, MatIcon, TranslateModule]
})
export class AlertsHistoryMobilePage implements OnInit {

  public alertsHistory: INotificationData[] = [];
  public isLoading = false;

  // The tutorial uses the same card template as real events, with local data only.
  readonly historyExampleAlert = {
    eventType: '01',
    timestamp: new Date(2026, 9, 15, 10, 24),
    managedAt: new Date(2026, 9, 15, 10, 26),
    facilities: { room: { name: '' }, bed: null },
    managedBy: 'example-operator',
    managed_by_info: { role: 'user' as UserRole }
  };

  public eventTypeToData: { [key: string]: { name: string, severity: string, svgIcon: string } } = {
    "01": {
      severity: "high",
      name: "Caduta",
      svgIcon: 'fall'
    },
    "02": {
      severity: "medium",
      name: "Entrata Stanza",
      svgIcon: 'room_in'
    },
    "03": {
      severity: "medium",
      name: "Uscita Stanza",
      svgIcon: 'room_out'
    },
    "04": {
      severity: "medium",
      name: "Entrata Letto",
      svgIcon: 'bed_in'
    },
    "05": {
      severity: "medium",
      name: "Uscita Letto",
      svgIcon: 'bed_out'
    },
    "06": {
      severity: "low",
      name: "Sensore Offline",
      svgIcon: 'device_offline'
    },
    "07": {
      severity: "low",
      name: "Sensore di Nuovo Online",
      svgIcon: 'device_online'
    },
    "08": {
      severity: "low",
      name: "Test",
      svgIcon: 'device_online'
    },
    "10": {
      severity: "low",
      name: "Ingresso Bagno",
      svgIcon: 'bathroom_in'
    }
  };

  constructor(
    private alertService: AlertsService,
    private facilitiesService: FacilitiesService,
    private toastService: ToastService,
    private translateService: TranslateService,
    public guide: GuideService
  ) {
    addIcons({ bedOutline, personCircleOutline, serverOutline, arrowForward, warningOutline, walkOutline, alertCircle });
  }
  ngOnInit() {
    this.loadAlerts();
  }

  /**
   * @description Funzione che va ad aggiornare i dati della table in pagina
   */
  loadAlerts(refreshData: boolean = false, reloadOnlyOnStatusChange: boolean = false, timeFilter: TimestampFilter = 'last24h') {
    if (refreshData && this.isLoading) {
      return;
    }
    this.isLoading = !reloadOnlyOnStatusChange && true;
    combineLatest([
      this.alertService.getAnalyticsAlerts$(timeFilter),
      this.facilitiesService.getUnits$()
    ]).pipe(
      take(1),
      tap(([alerts]) => {
        alerts = alerts.map((alert) => ({ ...alert, timestamp: new Date(alert.timestamp), managedAt: new Date(alert.managedAt) }))
        this.alertsHistory = alerts;
        this.isLoading = false;
        refreshData && this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.DATA_REFRESHED'), IonicColors.GREEN);
      }),
      catchError(err => {
        this.isLoading = false;
        refreshData && this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'), IonicColors.RED);
        throw err
      })
    ).subscribe()
  }

  // Helper per Colori e Icone
  getAlertColorClass(eventType: string): string {
    const severity = this.eventTypeToData[eventType].severity;
    switch (severity) {
      case 'low':
        return 'type-info';
      case 'medium':
        return 'type-warning';
      case 'high':
        return 'type-danger';
      default:
        return ''
    }
  }

  roleToText(role: UserRole) {
    if ((role as any) === 'system') {
      return "Autogestione"
    } else if (role === 'superuser') {
      return "Amministratore"
    } else if (role) {
      return "Utente"
    } else {
      return "-"
    }
  }

  // Calcola durata (es. "1h 11m")
  getDuration(start: Date, end: Date): string {
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins > 60) {
      const h = Math.floor(diffMins / 60);
      const m = diffMins % 60;
      return `${h}h ${m}m`;
    }
    return `${diffMins}m`;
  }
}
